// Slice thread: caricamento, ricerca, selezione, etichette, star, aggancio cliente.
// Logica identica a gmailStore.ts (grouping/sort via helpers).
import type { MailAdapter } from '../mailAdapter';
import type { MailMessage } from '../types';
import type { MailActions, MailSet, MailGet } from '../storeTypes';
import { buildThreadsFromMessages, countUnreadFromMessages } from '../helpers';

type ThreadsSlice = Pick<
  MailActions,
  'loadThreads' | 'searchThreads' | 'selectThread' | 'deselectThread' | 'markOpenThreadUnread' | 'clearKeptUnread' | 'setThreadLabel' | 'toggleStar' | 'linkThreadToClient'
>;

export function createThreadsSlice(set: MailSet, get: MailGet, adapter: MailAdapter): ThreadsSlice {
  const LOG = `[mailStore:${adapter.provider}]`;
  // Lettura automatica in corso all'apertura: "Segna come non letto" la aspetta,
  // altrimenti potrebbe arrivare dopo e rimettere la mail come letta.
  let pendingRead: Promise<void> | null = null;
  return {
    loadThreads: async (organizationId, userId, label) => {
      const activeLabel = label || get().activeLabel;
      // Spinner SOLO al primo caricamento; i refresh avvengono in background.
      const isFirstLoad = get().threads.length === 0;
      set({ threadsLoading: isFirstLoad, threadsError: null });

      try {
        const messages = await adapter.fetchMessages(organizationId, userId, activeLabel, 200);
        const threads = buildThreadsFromMessages(messages);
        // Il badge e` quello della Posta in arrivo: fuori dall'INBOX si tiene il precedente.
        const unreadCount = activeLabel === 'INBOX' ? countUnreadFromMessages(messages) : get().unreadCount;

        // Vista "Bozze": solo i thread che hanno una bozza
        const finalThreads = activeLabel === 'DRAFT'
          ? threads.filter(t => !!get().drafts[t.thread_id])
          : threads;

        set({ threads: finalThreads, threadsLoading: false, unreadCount });
      } catch (error: any) {
        console.error(`${LOG} Load threads error:`, error);
        set({ threadsLoading: false, threadsError: error.message });
      }
    },

    searchThreads: async (organizationId, userId, query) => {
      set({ searchQuery: query, threadsLoading: true, keptUnreadThreadIds: [] });

      try {
        if (!query.trim()) {
          await get().loadThreads(organizationId, userId);
          return;
        }

        const messages = await adapter.searchMessages(organizationId, userId, query);
        const threads = buildThreadsFromMessages(messages);
        set({ threads, threadsLoading: false });
      } catch (error: any) {
        console.error(`${LOG} Search error:`, error);
        set({ threadsLoading: false, threadsError: error.message });
      }
    },

    selectThread: async (threadId, organizationId, userId) => {
      // Se era da leggere resta in "Da leggere" anche dopo la lettura (come Gmail).
      const wasUnread = get().threads.some(t => t.thread_id === threadId && t.has_unread);
      const kept = get().keptUnreadThreadIds;
      set({
        selectedThreadId: threadId,
        messagesLoading: true,
        ...(wasUnread && !kept.includes(threadId) ? { keptUnreadThreadIds: [...kept, threadId] } : {}),
      });

      try {
        // 1. Carica subito le righe dal DB (gia` decifrate dall'adapter)
        const decrypted = await adapter.fetchThreadMessages(organizationId, userId, threadId);

        // Evita race: se l'utente ha aperto un altro thread, non sovrascrivere
        if (get().selectedThreadId !== threadId) return;
        set({ selectedMessages: decrypted, messagesLoading: false });

        // 2. Segna come letti (non bloccante)
        const unreadIds = decrypted.filter(m => !m.is_read).map(m => m.id);
        if (unreadIds.length > 0) {
          pendingRead = get().markAsRead(organizationId, userId, unreadIds);
        }

        // 3. Fetch completo per messaggi senza corpo o con allegati privi di attachmentId.
        // Il caso "has_attachments ma metadata vuoto" copre Outlook: il delta sync scrive
        // attachments_metadata: [] e i veri attachmentId arrivano solo dal full-fetch
        // (che li backfilla anche a DB). Per Gmail il sync scrive sempre i metadata
        // insieme a has_attachments, quindi il ramo non scatta (al piu` un refetch benigno).
        const missing = decrypted.filter(m =>
          (!m.body_html && !m.body_text) ||
          (m.has_attachments && (
            (m.attachments_metadata || []).length === 0 ||
            (m.attachments_metadata || []).some(a => !a.attachmentId)
          ))
        );
        if (missing.length > 0) {
          await Promise.all(
            missing.map(m => get().loadFullMessage(organizationId, userId, m.message_id))
          );

          if (get().selectedThreadId !== threadId) return;
          const updated = await adapter.fetchThreadMessages(organizationId, userId, threadId);
          if (get().selectedThreadId === threadId) {
            set({ selectedMessages: updated });
          }
        }
      } catch (error: any) {
        console.error(`${LOG} Select thread error:`, error);
        set({ messagesLoading: false });
      }
    },

    deselectThread: () => {
      set({ selectedThreadId: null, selectedMessages: [], replyingToMessageId: null, replyingToThreadId: null });
    },

    // Come Gmail: rimette da leggere l'ultimo messaggio della conversazione aperta e
    // torna all'elenco. La conversazione resta in "Da leggere" (keptUnreadThreadIds).
    markOpenThreadUnread: async (organizationId, userId) => {
      const { selectedThreadId, selectedMessages } = get();
      const last = selectedMessages[selectedMessages.length - 1];
      if (!selectedThreadId || !last) return false;
      if (pendingRead) await pendingRead.catch(() => undefined);
      await get().markAsUnread(organizationId, userId, [last.id]);
      const ok = get().threads.some(t => t.thread_id === selectedThreadId && t.has_unread)
        || !get().threads.some(t => t.thread_id === selectedThreadId);
      if (get().selectedThreadId === selectedThreadId) get().deselectThread();
      return ok;
    },

    clearKeptUnread: () => set({ keptUnreadThreadIds: [] }),

    setThreadLabel: async (organizationId, userId, threadId, labelId, add) => {
      const thread = get().threads.find(t => t.thread_id === threadId);
      if (!thread) return;
      // Aggiornamento ottimistico locale
      set({
        threads: get().threads.map(t => t.thread_id === threadId ? {
          ...t,
          messages: t.messages.map(m => ({
            ...m,
            label_ids: add
              ? Array.from(new Set([...(m.label_ids || []), labelId]))
              : (m.label_ids || []).filter(l => l !== labelId),
          })),
        } : t),
      });
      try {
        await Promise.all(thread.messages.map(m =>
          adapter.modify(organizationId, userId, m.message_id, add ? 'addLabel' : 'removeLabel', labelId)
        ));
      } catch (error: any) {
        console.error(`${LOG} setThreadLabel error:`, error);
      }
    },

    toggleStar: async (organizationId, userId, threadId, starred) => {
      const thread = get().threads.find(t => t.thread_id === threadId);
      if (!thread) return;
      // Optimistic update
      const optimistic = get().threads.map(t =>
        t.thread_id === threadId ? { ...t, is_starred: starred } : t
      );
      set({ threads: optimistic });
      try {
        const latest = thread.latest_message;
        await adapter.modify(organizationId, userId, latest.message_id, starred ? 'star' : 'unstar');
        await adapter.updateStarFlag(organizationId, userId, threadId, starred);
      } catch (error: any) {
        console.error(`${LOG} Toggle star error:`, error);
        // Rollback
        const rolled = get().threads.map(t =>
          t.thread_id === threadId ? { ...t, is_starred: !starred } : t
        );
        set({ threads: rolled });
      }
    },

    linkThreadToClient: async (organizationId, userId, threadId, clientId, clientInfo) => {
      try {
        await adapter.updateThreadClient(organizationId, userId, threadId, clientId);
        // Oltre a client_id si aggiorna anche la scheda joinata: e` quella che la
        // schermata legge per mostrare il nome. Scollegando (clientId null) va
        // azzerata, altrimenti il nome resterebbe a video su un thread ormai libero.
        const applyLink = (m: MailMessage): MailMessage => ({
          ...m,
          client_id: clientId,
          client: clientId ? (clientInfo ?? m.client ?? null) : null,
        });
        set({
          threads: get().threads.map(t => t.thread_id === threadId ? {
            ...t,
            messages: t.messages.map(applyLink),
          } : t),
          selectedMessages: get().selectedThreadId === threadId
            ? get().selectedMessages.map(applyLink)
            : get().selectedMessages,
        });
      } catch (error: any) {
        console.error(`${LOG} linkThreadToClient error:`, error);
      }
    },
  };
}
